import { useEffect, useRef } from "react";

/**
 * Horizontal kinetic pipeline — Discover → Structure → Coordinate → Realize.
 * Used in: Home hero.
 */
export default function PipelineH({ height = 280, hoverStage = null }) {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let raf;
    const dpr = window.devicePixelRatio || 1;
    const resize = () => {
      canvas.width = canvas.clientWidth * dpr;
      canvas.height = canvas.clientHeight * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    window.addEventListener("resize", resize);

    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const N = reduce ? 120 : 380;
    const pts = Array.from({ length: N }, () => ({
      x: Math.random() * canvas.clientWidth,
      y: Math.random() * 100,
      vx: 0.6 + Math.random() * 0.9,
      seed: Math.random() * Math.PI * 2,
      size: 0.8 + Math.random() * 1.4,
    }));

    const lerp = (a, b, t) => a + (b - a) * t;

    const draw = () => {
      const w = canvas.clientWidth, h = canvas.clientHeight;
      ctx.clearRect(0, 0, w, h);

      [0.15, 0.4, 0.65, 0.9].forEach((p, i) => {
        const sx = p * w;
        ctx.strokeStyle = hoverStage === i ? "rgba(157,180,255,0.5)" : "rgba(255,255,255,0.07)";
        ctx.setLineDash([2, 6]);
        ctx.beginPath(); ctx.moveTo(sx, 20); ctx.lineTo(sx, h - 20); ctx.stroke();
        ctx.setLineDash([]);
      });

      pts.forEach((p) => {
        const speedMod =
          hoverStage !== null &&
          p.x / w > [0.0, 0.27, 0.52, 0.77][hoverStage] &&
          p.x / w < [0.27, 0.52, 0.77, 1][hoverStage]
            ? 0.25
            : 1;
        p.x += p.vx * speedMod;
        if (p.x > w + 10) { p.x = -10; p.y = Math.random() * 100; }
        const t = p.x / w;
        let yPct;
        if (t < 0.27) yPct = p.y + Math.sin(p.seed + p.x * 0.04) * 14;
        else if (t < 0.52) {
          const a = (t - 0.27) / 0.25;
          const tr = Math.floor(p.seed * 2.4) % 6;
          yPct = lerp(p.y + Math.sin(p.seed + p.x * 0.04) * 14, 12 + (tr / 5) * 78, a);
        } else if (t < 0.77) {
          const tr = Math.floor(p.seed * 2.4) % 6;
          yPct = 12 + (tr / 5) * 78 + Math.sin(p.x * 0.05 + p.seed) * 2;
        } else {
          const tr = Math.floor(p.seed * 2.4) % 6;
          yPct = 12 + (tr / 5) * 78;
        }
        const y = (yPct / 100) * h;
        const r = Math.round(lerp(232, 220, t));
        const g = Math.round(lerp(180, 230, t));
        const b = Math.round(lerp(120, 240, t));
        ctx.fillStyle = `rgba(${r},${g},${b},${0.35 + t * 0.55})`;
        ctx.beginPath(); ctx.arc(p.x, y, p.size + (t > 0.77 ? 0.4 : 0), 0, Math.PI * 2); ctx.fill();
        if (t > 0.8) {
          ctx.strokeStyle = `rgba(220,230,240,${(t - 0.8) * 0.6})`;
          ctx.lineWidth = 0.5;
          ctx.beginPath(); ctx.moveTo(p.x - 8, y); ctx.lineTo(p.x + 8, y); ctx.stroke();
        }
      });
      raf = requestAnimationFrame(draw);
    };
    draw();
    return () => { cancelAnimationFrame(raf); window.removeEventListener("resize", resize); };
  }, [hoverStage]);

  return (
    <canvas
      ref={canvasRef}
      data-testid="pipeline-h"
      className="w-full block"
      style={{ width: "100%", height }}
    />
  );
}
